import { useEffect, useState, useCallback } from "react";
import { api, fmtBytes, fmtNum, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { HardDrive, Download, Trash2, FolderOpen, FileJson, FileSpreadsheet, FileText, MonitorDown } from "lucide-react";

const FMT_ICON = { json: FileJson, jsonl: FileJson, csv: FileSpreadsheet, txt: FileText };

export default function FileVault() {
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.listFiles();
      setFiles(d.files);
      setStats(d.stats);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (f) => {
    await api.deleteFile(f.id);
    toast.success(`Deleted ${f.filename}`);
    load();
  };

  const usedPct = stats ? Math.min(100, (stats.total_bytes / Math.max(stats.disk_total_bytes, 1)) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1400px]">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Files &amp; Storage</h2>
          <p className="text-sm text-slate-500 mt-1">Saved dataset exports, kept on disk and ready to download.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="files-refresh-button" className="border-border hover:bg-slate-800">
          Refresh
        </Button>
      </header>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-5 space-y-4" data-testid="storage-meter">
        <div className="flex items-center gap-2">
          <HardDrive className="h-4 w-4 text-cyan-400" />
          <h3 className="font-display font-bold text-base">Storage</h3>
          <span className="ml-auto font-mono text-xs text-slate-400 tabular-nums">
            {fmtBytes(stats?.total_bytes || 0)} used · {fmtNum(stats?.count || 0)} files
          </span>
        </div>
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 transition-[width] duration-700"
            style={{ width: `${Math.max(1.5, usedPct)}%` }} />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-mono text-slate-600">
          <span>dir: <span className="text-slate-400">{stats?.directory || "—"}</span></span>
          <span>disk free: <span className="text-slate-400">{fmtBytes(stats?.disk_free_bytes || 0)}</span></span>
        </div>
        <div className="flex items-start gap-2 rounded-md border border-border bg-[#0E1420]/70 p-3">
          <MonitorDown className="h-3.5 w-3.5 text-purple-400 mt-0.5 shrink-0" />
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Files live on the server that runs DataForge AI. In the desktop / fully-local setup that server is your own
            machine, so exports land in <span className="font-mono text-slate-400">backend/storage</span> and stay there
            permanently — override the location with the <span className="font-mono text-slate-400">STORAGE_DIR</span> env var.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
          <FolderOpen className="h-4 w-4 text-amber-400" />
          <h3 className="font-display font-bold text-base">Saved Exports</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="font-mono text-[11px] uppercase text-slate-500">File</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500">Dataset</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500 text-right">Records</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500 text-right">Size</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500">Created</TableHead>
                <TableHead className="font-mono text-[11px] uppercase text-slate-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="files-table-body">
              {files.length === 0 && (
                <TableRow className="border-border">
                  <TableCell colSpan={6} className="text-center text-slate-600 font-mono text-sm py-10">
                    {loading ? "Loading…" : "No saved files yet — export a dataset from Dataset Studio with 'Save to Files'."}
                  </TableCell>
                </TableRow>
              )}
              {files.map((f) => {
                const Icon = FMT_ICON[f.fmt] || FileText;
                return (
                  <TableRow key={f.id} className="border-border hover:bg-slate-800/40" data-testid={`file-row-${f.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-cyan-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="truncate text-sm text-slate-200 max-w-[320px]">{f.filename}</p>
                          <p className="text-[10px] font-mono text-slate-600">
                            {f.options?.shape} · {f.options?.chunk_tokens ? `${f.options.chunk_tokens}tok chunks` : "per page"}
                            {f.duplicates_removed ? ` · -${f.duplicates_removed} dupes` : ""}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-400 max-w-[200px] truncate">{f.crawl_name}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-amber-400">{fmtNum(f.records)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-300">{fmtBytes(f.size_bytes)}</TableCell>
                    <TableCell className="font-mono text-[11px] text-slate-500">{f.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Badge variant="outline" className="mr-2 font-mono text-[10px] border-slate-600 text-slate-400 uppercase">{f.fmt}</Badge>
                      <Button size="sm" variant="ghost" data-testid={`file-download-${f.id}`}
                        onClick={() => downloadFile(api.fileDownloadUrl(f.id), f.filename).catch(() => toast.error("Download failed"))}
                        className="h-7 text-slate-400 hover:text-cyan-300">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`file-delete-${f.id}`}
                        onClick={() => remove(f)} className="h-7 text-slate-500 hover:text-rose-400">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
